<app-review-form-header>
  <div title>
    <span class="text-sm text-gray-500" data-testid="assignment-title">{{ incidentType() }}</span>
    <span class="text-sm text-gray-500 ml-2">self-service</span>
  </div>
  <div actions class="flex items-center gap-2">
    <app-pan-button color="primary" icon="arrow_back" (click)="back()">Back</app-pan-button>

    <app-pan-button
      color="primary"
      icon="edit"
      (click)="reviewModal.show()"
      [state]="isSubmitting() ? 'loading' : 'default'"
      [disabled]="!assignment() || isSubmitting()"
      data-testid="completeReview"
    >
      Complete Review
    </app-pan-button>

    <app-review-report-problem [reviewId]="assignmentId()"></app-review-report-problem>

    @if (debug.canShowDebug()) {
      <app-pan-button
        icon="bug_report"
        [fill]="debug.isDebugVisible() ? 'filled' : 'outline'"
        [color]="debug.isDebugVisible() ? 'primary' : 'default'"
        (click)="debug.toggleDebug()"
      ></app-pan-button>
    }
  </div>
</app-review-form-header>

<app-pan-modal [shouldBodyScroll]="true" #reviewModal [topLevel]="false" size="lg">
  <div header>Submit review</div>

  <div body>
    @if (formTemplate; as tpl) {
      <ng-container [ngTemplateOutlet]="tpl"></ng-container>
    }

    <swal
      #submittedSwal
      title="Incident submitted 🎉"
      text="Thank you for the review!"
      icon="success"
      denyButtonText="Go back"
      confirmButtonText="Queue next review"
      [showDenyButton]="true"
      [showConfirmButton]="hasAvailableAssignments()"
      [focusCancel]="true"
      (confirm)="redirectToNextAssignment()"
      (deny)="back()"
    >
      <div *swalPortal="swalTargets.content" class="space-y-2">
        @if (location()?.name) {
          <div>You are doing reviews for {{ location()?.name }}</div>
        }
        @if (isLoadingAvailableAssignments()) {
          <app-pan-spinner></app-pan-spinner>
        } @else {
          @if (hasAvailableAssignments()) {
            <div class="flex items-center justify-center gap-1">
              <mat-icon class="text-success">check</mat-icon>
              <span>Available assignment</span>
            </div>
          } @else {
            <div>No available assignments.</div>
          }
        }
        @if (nextReviewError()) {
          <div class="text-red-600 px-4 italic">{{ nextReviewError() }}</div>
        }
      </div>
    </swal>
    <app-review-submission-failed-modal #failedModal [errorMessage]="submitFailureError()"></app-review-submission-failed-modal>
  </div>

  <div footer>
    <app-pan-button
      panModalClose
      color="primary"
      (click)="submit()"
      [disabled]="isSubmitting() || !reviewFormService.isFormSubmittable()"
      [state]="isSubmitting() ? 'loading' : 'default'"
      data-testid="submitReview"
    >
      Submit
    </app-pan-button>
    <app-pan-button panModalClose>Cancel</app-pan-button>
  </div>
</app-pan-modal>

@if (isLoading()) {
  <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
}

@if (isCompleted()) {
  <app-pan-inline-alert type="info" class="m-2"> This assignment has already been completed.</app-pan-inline-alert>
}

@if (errorMessage()) {
  <app-pan-inline-alert type="danger" class="m-2">
    {{ errorMessage() }}
  </app-pan-inline-alert>
}

@if (debug.isDebugVisible()) {
  <app-pan-card header="Debugging tools">
    <div>Selected location: {{ preferredLocationId() }}</div>

    <div>
      <app-pan-button (click)="checkAvailableAssignments()">Check available assignments</app-pan-button>
      @if (!isLoadingAvailableAssignments()) {
        <div>Has available assignments: {{ hasAvailableAssignments() }}</div>
      }
    </div>

    <div>
      <app-pan-button (click)="loadDebugNextAssignmentLink()">Get next assignment link</app-pan-button>
      <div>Next assignment link: {{ debugNextAssignmentLink() }}</div>
    </div>
  </app-pan-card>
}

@switch (incidentType()) {
  @case (ReviewSubjectType.Cancellation) {
    <app-transaction-incident-form
      [locationTimezone]="assignment()?.location_timezone"
      (contextReady)="onContextReady($event)"
      (formRefReady)="formTemplate = $event"
    />
  }
  @case (ReviewSubjectType.Underringing) {
    <app-transaction-incident-form
      [locationTimezone]="assignment()?.location_timezone"
      (contextReady)="onContextReady($event)"
      (formRefReady)="formTemplate = $event"
    />
  }
  @case (ReviewSubjectType.Sideloaded) {
    <app-sideloaded-incident-form
      [locationTimezone]="assignment()?.location_timezone"
      (contextReady)="onContextReady($event)"
      (formRefReady)="formTemplate = $event"
    />
  }
  @case (ReviewSubjectType.Deepdive) {
    <app-sideloaded-incident-form
      [locationTimezone]="assignment()?.location_timezone"
      (contextReady)="onContextReady($event)"
      (formRefReady)="formTemplate = $event"
    />
  }
  @case (ReviewSubjectType.Walkout) {
    <app-walkout-incident-form
      [locationTimezone]="assignment()?.location_timezone"
      (contextReady)="onContextReady($event)"
      (formRefReady)="formTemplate = $event"
    />
  }
}
